import { Card, Button, DatePicker, Modal, Input } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import moment from "moment-timezone";
import Loading from "../common/Loading";
import { TableHelper } from "../../util/TableHelper";
import { useAdvanceBookingRequest } from "./index.hook";
import { General } from "../../util/General";
import styles from "./index.module.less";
import KeyFobAllocationModal from "./KeyFobAllocationModal";
import Draggable from "react-draggable";
import { useReactDraggable } from "../../hooks/useReactDraggable";
import PaymentDetailsModal from "./PaymentSummary";
import type { FormattedABRRequest } from "../../shared/interface/advanceBookingRequest";
import PayAndParkModal from "./PayAndParkModal";

const AdvanceBookingRequest = () => {
  const config = useAdvanceBookingRequest();
  const { bounds, onStart } = useReactDraggable();

  const formatTime = (time: string | null) => (time ? moment(time).format("HH:mm") : "");

  const renderTime = (key: keyof FormattedABRRequest) => (text: any) => <div>{formatTime(text)}</div>;
  const valueSorter = (key: keyof FormattedABRRequest) => ({ compare: TableHelper.valueCompare(key) });

  const columns: ColumnsType<FormattedABRRequest> = [
    {
      align: "right",
      title: "Sr No.",
      dataIndex: "id",
      key: "id",
      render: TableHelper.renderNumberValue,
      sorter: valueSorter("id")
    },
    {
      align: "left",
      title: "Booking ID",
      dataIndex: "booking_id",
      key: "booking_id",
      render: (text, record) => <span className={record.id === config.selectedRecord?.id ? styles["modal__description"] : ""}>{TableHelper.getValue(text)}</span>,
      sorter: valueSorter("booking_id")
    },
    {
      align: "left",
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: TableHelper.renderText,
      sorter: valueSorter("username")
    },
    {
      align: "left",
      title: "Vehicle Type",
      dataIndex: "vehicle_type_name",
      key: "vehicle_type_name",
      render: TableHelper.renderText,
      sorter: valueSorter("vehicle_type_name")
    },
    {
      align: "right",
      title: "Mobile Number",
      dataIndex: "mobile_number",
      key: "mobile_number",
      render: TableHelper.renderText,
      sorter: valueSorter("mobile_number")
    },
    {
      title: "Booking Time",
      children: [
        {
          align: "right",
          title: "In-Time",
          dataIndex: "parking_start_date",
          key: "parking_start_date",
          render: renderTime("parking_start_date"),
          sorter: { compare: TableHelper.dateSorter("parking_start_date") }
        },
        {
          align: "right",
          title: "Out-Time",
          dataIndex: "parking_end_date",
          key: "parking_end_date",
          render: renderTime("parking_end_date"),
          sorter: { compare: TableHelper.dateSorter("parking_end_date") }
        }
      ]
    },
    {
      title: "Parking Time",
      children: [
        {
          align: "right",
          title: "In-Time",
          dataIndex: "parking_in_time",
          key: "parking_in_time",
          render: renderTime("parking_in_time"),
          sorter: { compare: TableHelper.dateSorter("parking_in_time") }
        },
        {
          align: "right",
          title: "Out-Time",
          dataIndex: "parking_out_time",
          key: "parking_out_time",
          render: renderTime("parking_out_time"),
          sorter: { compare: TableHelper.dateSorter("parking_out_time") }
        }
      ]
    },
    {
      align: "right",
      title: "KeyFob",
      dataIndex: "key_fob_token",
      key: "key_fob_token",
      render: TableHelper.renderNumberValue,
      sorter: valueSorter("key_fob_token")
    },
    {
      align: "right",
      title: "Parking Amount",
      dataIndex: "parking_amount",
      key: "parking_amount",
      render: TableHelper.renderNumberValue,
      sorter: valueSorter("parking_amount")
    },
    {
      align: "right",
      title: "Valet Amount",
      dataIndex: "valet_parking_amount",
      key: "valet_parking_amount",
      render: TableHelper.renderNumberValue,
      sorter: valueSorter("valet_parking_amount")
    },
    {
      align: "left",
      title: "Valet Parking",
      dataIndex: "is_valet_parking",
      key: "is_valet_parking",
      render: (text) => <div>{text ? "Yes" : "No"}</div>,
      sorter: valueSorter("is_valet_parking")
    },
    {
      align: "left",
      title: "Payment",
      dataIndex: "payment_stage",
      key: "payment_stage",
      render: (text) => <div>{text}</div>,
      sorter: valueSorter("payment_stage")
    },
    {
      align: "center",
      title: "Actions",
      fixed: "right",
      render: (_, record) => {
        const actions = [
          {
            label: "Booking",
            onClick: () => config.handleAddABRRequest(record),
            disabled: !record?.isBookingActive
          },
          {
            label: "Retrieval",
            onClick: () => config.handleRaiseRetrievalRequest(record),
            disabled: !record?.isRetrievalActive
          },
          {
            label: "Cancel",
            onClick: () => config.handleCancelABRRequest(record),
            disabled: !record?.isCancelActive
          },
          {
            label: "Complete",
            onClick: () => config.handlePaymentSummeryModalOpen(record),
            disabled: !record?.isCompleteActive
          }
        ];

        return (
          <div className={styles.actionAllDevice}>
            <div className={styles.actionGroup}>
              {actions.slice(0, 2).map(({ label, onClick, disabled }) => (
                <Button key={label} className={`${styles.actionButton} gx-mb-0 button-gradiant`} size="small" onClick={onClick} disabled={disabled}>
                  {label}
                </Button>
              ))}
            </div>
            <div className={styles.actionGroup}>
              {actions.slice(2).map(({ label, onClick, disabled }) => (
                <Button key={label} className={`${styles.actionButton} gx-mb-0 button-gradiant`} size="small" onClick={onClick} disabled={disabled}>
                  {label}
                </Button>
              ))}
            </div>
          </div>
        );
      }
    }
  ];

  const renderModal = (title: string, visible: boolean, onOk: () => void, onCancel: () => void, bookingId?: string) => (
    <Modal
      title={<div className="modal-container">{title}</div>}
      open={visible}
      onOk={onOk}
      onCancel={onCancel}
      confirmLoading={config.saving}
      okText="Yes"
      cancelText="No"
      width="580px"
      destroyOnClose
      maskClosable={false}
      wrapClassName="delete-modal"
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
      okButtonProps={{ disabled: config.saving, loading: config.saving }}
    >
      <h4>
        Are you sure you want to {title.toLowerCase()} with ID <span className={styles.modal__description}>{bookingId ?? ""}?</span>
      </h4>
    </Modal>
  );

  return (
    <Card
      className="gx-card card--height"
      title={
        <div className={`gx-title-container`}>
          <div className={styles["left-section"]}>
            <span className="card-title-style gx-mr-3">ADVANCE BOOKING REQUEST</span>
            <Button onClick={config.handleRefreshList} className={`button-gradiant gx-mb-0 add-button`} loading={config.loading}>
              Refresh
            </Button>
          </div>
          <div className={styles["right-section"]}>
            <Input.Search className="order-1 gx-mb-0 search-component__width" allowClear placeholder="Search..." value={config.searchedText} onChange={(e) => config.handleChangeSearch(e.target.value)} onSearch={config.handleChangeSearch} />
            <DatePicker format="DD-MM-YYYY" defaultValue={moment()} onChange={config.handleDateChange} getPopupContainer={(trigger) => trigger.parentElement || document.body} disabledDate={General.disabledPastDate} />
            <Button onClick={config.handleEnablePayAndParkModal} className={`button-gradiant gx-mb-0 add-button`}>
              Pay & Park
            </Button>
          </div>
        </div>
      }
    >
      {config.loading ? <Loading /> : <Table className="gx-table-responsive" size="small" columns={columns} dataSource={config.filteredTableData ?? []} pagination={config.pagination} bordered onChange={config.handleChangePagination} rowKey={(record) => record.id} />}

      <KeyFobAllocationModal data={config.selectedRecord} handleRefreshList={config.handleRefreshList} handleOnClose={config.handleOnClose} isModalVisible={config.isModalVisible} />

      {renderModal("Cancel ABR Request", config.isCancelRequestModalVisible, config.handleCancelRequestConfirm, config.handleCancelRequestClose, config.selectedRecord?.booking_id)}
      {renderModal("Raise Retrieval Request", config.isRetrievalRequestModalVisible, config.handleRetrievalRequestConfirm, config.handleRetrievalRequestClose, config.selectedRecord?.booking_id)}

      <PaymentDetailsModal data={config.selectedRecord} handleRefreshList={config.handleRefreshList} isVisible={config.isPaymentModalVisible} handleOnClose={config.handlePaymentSummeryModalClose} summaryData={config.summaryData} loading={config.paymentSummaryLoading} />
      {config.isPayAndParkModalVisible && <PayAndParkModal key={Math.random()} handleOnClose={config.handleDisablePayAndParkModal} handleRefreshList={config.handleRefreshList} selectedDate={config.selectedDate} />}
    </Card>
  );
};

export default AdvanceBookingRequest;
